import React, { useState, useEffect, useRef } from 'react';
import { referencesAPI } from '../services/api';
import './EventModal.css';

const EventModal = ({ isOpen, onClose, onSave, onDelete, event, mode, defaultDate, objects = [], defaultObjectId, canEditObjects = true, canEditStatus = true }) => {
  const addObjectSelectRef = useRef(null);
  const [organizations, setOrganizations] = useState([]);
  const [sportTypes, setSportTypes] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    description: '',
    cloudUrl: '',
    status: 'not_approved',
    objectIds: [],
    objectDates: {},
    organizationId: '',
    sportTypeId: ''
  });

  // Формат для datetime-local в локальном времени (не UTC)
  const toDatetimeLocal = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${h}:${min}`;
  };

  useEffect(() => {
    if (event) {
      const status = event.status || event.approved === true ? 'approved' : 'not_approved';
      const objectIds = Array.isArray(event.objectIds) ? event.objectIds : [];
      const mainStart = event.startDate ? toDatetimeLocal(event.startDate) : (event.start_date ? toDatetimeLocal(event.start_date) : '');
      const mainEnd = event.endDate ? toDatetimeLocal(event.endDate) : (event.end_date ? toDatetimeLocal(event.end_date) : '');
      const objectDates = {};
      if (event.objectDates && typeof event.objectDates === 'object') {
        for (const oid of objectIds) {
          const d = event.objectDates[oid] || event.objectDates[String(oid)];
          const objStart = d && d.startDate ? toDatetimeLocal(d.startDate) : '';
          const objEnd = d && d.endDate ? toDatetimeLocal(d.endDate) : '';
          // Храним только даты, явно отличающиеся от основных — иначе при смене только основных дат
          // в API уходили бы старые даты по объекту и event_objects не обновлялись бы
          objectDates[oid] = {
            startDate: objStart && objStart !== mainStart ? objStart : '',
            endDate: objEnd && objEnd !== mainEnd ? objEnd : ''
          };
        }
      }
      const orgId = event.organizationId ?? event.organization_id ?? '';
      const sportId = event.sportTypeId ?? event.sport_type_id ?? '';
      setFormData({
        name: event.name || '',
        startDate: mainStart,
        endDate: mainEnd,
        description: event.description || '',
        cloudUrl: event.cloud_url ?? event.cloudUrl ?? '',
        status,
        objectIds,
        objectDates,
        organizationId: orgId !== null && orgId !== undefined ? String(orgId) : '',
        sportTypeId: sportId !== null && sportId !== undefined ? String(sportId) : ''
      });
    } else if (mode === 'create' && defaultDate) {
      const start = new Date(defaultDate);
      const objectIds = defaultObjectId ? [defaultObjectId] : (objects.length ? [objects[0].id] : []);
      setFormData({
        name: '',
        startDate: toDatetimeLocal(start.toISOString()),
        endDate: '',
        description: '',
        cloudUrl: '',
        status: 'not_approved',
        objectIds,
        objectDates: {},
        organizationId: '',
        sportTypeId: ''
      });
    } else {
      setFormData({
        name: '',
        startDate: '',
        endDate: '',
        description: '',
        cloudUrl: '',
        status: 'not_approved',
        objectIds: defaultObjectId ? [defaultObjectId] : (objects.length ? [objects[0].id] : []),
        objectDates: {},
        organizationId: '',
        sportTypeId: ''
      });
    }
  }, [event, isOpen, mode, defaultDate, defaultObjectId, objects]);

  // Загрузка организаций при открытии модалки
  useEffect(() => {
    if (isOpen) {
      referencesAPI.getOrganizations().then(setOrganizations).catch(() => setOrganizations([]));
    }
  }, [isOpen]);

  // Загрузка видов спорта при выборе организатора
  useEffect(() => {
    if (!formData.organizationId) {
      setSportTypes([]);
      return;
    }
    referencesAPI.getSportTypes(parseInt(formData.organizationId, 10)).then(setSportTypes).catch(() => setSportTypes([]));
  }, [formData.organizationId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'organizationId') {
      setFormData({ ...formData, [name]: value, sportTypeId: '' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const addObjectId = (objectId) => {
    if (!objectId || formData.objectIds.includes(objectId)) return;
    const objectDates = { ...formData.objectDates };
    objectDates[objectId] = { startDate: formData.startDate, endDate: formData.endDate };
    setFormData({ ...formData, objectIds: [...formData.objectIds, objectId], objectDates });
  };

  const removeObjectId = (objectId) => {
    if (!canEditObjects) return;
    const ids = formData.objectIds.filter(id => id !== objectId);
    const objectDates = { ...formData.objectDates };
    delete objectDates[objectId];
    setFormData({ ...formData, objectIds: ids.length ? ids : formData.objectIds, objectDates });
  };

  const setObjectDate = (objectId, field, value) => {
    const objectDates = { ...formData.objectDates };
    if (!objectDates[objectId]) objectDates[objectId] = { startDate: '', endDate: '' };
    objectDates[objectId][field] = value;
    setFormData({ ...formData, objectDates });
  };

  const getObjectStartEnd = (objectId) => {
    const d = formData.objectDates[objectId];
    return {
      start: (d && d.startDate) ? d.startDate : formData.startDate,
      end: (d && d.endDate) ? d.endDate : formData.endDate
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Валидация дат
    if (new Date(formData.endDate) <= new Date(formData.startDate)) {
      alert('Дата окончания должна быть позже даты начала');
      return;
    }
    if (!formData.objectIds || formData.objectIds.length === 0) {
      alert('Выберите хотя бы один объект');
      return;
    }
    // Валидация дат по объектам
    const objectDates = {};
    for (const oid of formData.objectIds) {
      const { start, end } = getObjectStartEnd(oid);
      if (start && end && new Date(end) <= new Date(start)) {
        const obj = objects.find(o => o.id === oid);
        alert(`На объекте «${obj ? obj.name : oid}» дата окончания должна быть позже даты начала`);
        return;
      }
      objectDates[oid] = { startDate: start || '', endDate: end || '' };
    }
    onSave({ ...formData, objectDates });
    onClose();
  };

  const handleDelete = () => {
    // Подтверждение удаления выполняется на уровне родительского компонента (SportsScheduleTable).
    // Здесь просто вызываем обработчик.
    if (onDelete) {
      onDelete();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>{mode === 'create' ? 'Создать мероприятие' : 'Редактировать мероприятие'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Название мероприятия:</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="date-fields-container">
            <div className="form-group">
              <label>Дата и время начала:</label>
              <input
                type="datetime-local"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Дата и время окончания:</label>
              <input
                type="datetime-local"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Описание:</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Ссылка на документы (облако):</label>
            <input
              type="url"
              name="cloudUrl"
              value={formData.cloudUrl}
              onChange={handleChange}
              placeholder="https://..."
            />
          </div>

          <div className="form-group">
            <label>Организатор:</label>
            <select
              name="organizationId"
              value={formData.organizationId}
              onChange={handleChange}
            >
              <option value="">— не выбрано —</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Вид спорта:</label>
            <select
              name="sportTypeId"
              value={formData.sportTypeId}
              onChange={handleChange}
              disabled={!formData.organizationId}
            >
              <option value="">— не выбрано —</option>
              {sportTypes.map((st) => (
                <option key={st.id} value={st.id}>{st.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Статус:</label>
            {canEditStatus ? (
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="not_approved">Не утверждено</option>
                <option value="approved">Утверждено</option>
              </select>
            ) : (
              <div>
                {formData.status === 'approved' ? 'Утверждено (только для администратора)' : 'Не утверждено'}
              </div>
            )}
          </div>

          {objects.length > 0 && (
            <div className="form-group">
              <label>Объекты (мероприятие отображается на выбранных){!canEditObjects && ' (только просмотр)'}:</label>
              <div className="event-objects-added">
                {formData.objectIds.map(id => {
                  const obj = objects.find(o => o.id === id);
                  if (!obj) return null;
                  const { start, end } = getObjectStartEnd(id);
                  return (
                    <div key={obj.id} className="event-object-block">
                      <div className="event-object-tag">
                        <span>{obj.name}</span>
                        {canEditObjects && (
                          <button type="button" className="event-object-remove" onClick={() => removeObjectId(obj.id)} title="Удалить">×</button>
                        )}
                      </div>
                      <div className="event-object-dates">
                        <label className="event-object-date-label">Начало:</label>
                        <input
                          type="datetime-local"
                          className="event-object-datetime"
                          value={start}
                          onChange={(e) => setObjectDate(id, 'startDate', e.target.value)}
                          disabled={!canEditObjects}
                        />
                        <label className="event-object-date-label">Окончание:</label>
                        <input
                          type="datetime-local"
                          className="event-object-datetime"
                          value={end}
                          onChange={(e) => setObjectDate(id, 'endDate', e.target.value)}
                          disabled={!canEditObjects}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {canEditObjects && (
                <div className="event-objects-add-row">
                  <select
                    ref={addObjectSelectRef}
                    className="event-object-select"
                    value=""
                    onChange={(e) => {
                      const id = e.target.value ? parseInt(e.target.value, 10) : null;
                      if (id) addObjectId(id);
                      e.target.value = '';
                    }}
                  >
                    <option value="">— добавить объект —</option>
                    {objects
                      .filter(o => !formData.objectIds.includes(o.id))
                      .map(o => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                  </select>
                  <button type="button" className="event-object-add-btn" onClick={() => {
                    const select = addObjectSelectRef.current;
                    if (select && select.value) {
                      addObjectId(parseInt(select.value, 10));
                      select.value = '';
                    }
                  }} title="Добавить объект">+</button>
                </div>
              )}
            </div>
          )}

          <div className="modal-buttons">
            <button type="submit" className="save-button">Сохранить</button>
            {mode === 'edit' && onDelete && (
              <button type="button" className="delete-button" onClick={handleDelete}>Удалить</button>
            )}
            <button type="button" className="cancel-button" onClick={onClose}>Отмена</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventModal; 